import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Globe, LogOut, ArrowRight, Receipt, LifeBuoy } from "lucide-react";
import { Container } from "@/components/ui-kit";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";
import { fetchCustomerWebsites, selectCustomerWebsite } from "@/lib/api";

const STATUS_LABEL = { offline_concept: "Concept — not yet active", proposal_accepted: "Proposal accepted", online_client: "Live", rejected: "Inactive" };

export default function CustomerDashboard() {
  const { token, customer, checking, isLoggedIn, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [websites, setWebsites] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    // Clear a transient failure from an earlier auth/API race before retrying the
    // account load. Otherwise a successful retry can render complete data while
    // leaving a stale "Could not load your account" banner visible.
    setError("");
    fetchCustomerWebsites(token)
      .then(setWebsites)
      .catch(() => setError("Could not load your account."));
  }, [token]);

  if (checking) return null;
  if (!isLoggedIn) return <Navigate to="/account/login" replace />;

  const openWebsite = async (website) => {
    try {
      const result = await selectCustomerWebsite(token, website.slug);
      window.location.assign(result.redirect_to);
    } catch (err) { setError(err.response?.data?.detail || "Could not switch website."); }
  };
  const signOut = () => { logout(); navigate("/account/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Container className="py-12">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
          <div><p className="text-xs font-bold uppercase tracking-[0.16em] text-[#10B981]">My Account</p><h1 className="mt-1 text-2xl font-bold font-display text-[#0F172A]">{customer?.display_name || "Your websites"}</h1></div>
          <div className="flex gap-2"><Link data-testid="customer-dashboard-support-link" to="/account/support" className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm font-medium"><LifeBuoy size={15} /> Support</Link><button data-testid="customer-dashboard-logout-button" onClick={signOut} className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm font-medium"><LogOut size={15} /> Sign out</button></div>
        </div>
        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
        <section>
          <h2 className="text-xl font-bold font-display text-[#0F172A]">My Websites</h2>
          {websites === null ? <p className="mt-4 text-slate-500">Loading…</p> : websites.length === 0 ? (
            <div className="mt-4 rounded-2xl border bg-white p-10 text-center text-slate-600"><p>No websites are linked yet.</p><Link to="/plans" className="mt-4 inline-block font-semibold text-emerald-600">Choose a plan</Link></div>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">{websites.map((w) => (
              <article key={w.slug} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5 text-[#10B981]"><Globe size={18} /><span className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">{w.category}</span>{w.is_default && <span className="ml-auto rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold">DEFAULT</span>}</div>
                <h3 className="mt-2 font-bold font-display">{w.business_name}</h3><p className="mt-1 text-sm text-slate-600">{STATUS_LABEL[w.status] || w.status}</p>
                <div className="mt-4 flex flex-wrap gap-3"><button data-testid={`customer-open-website-${w.slug}`} onClick={() => openWebsite(w)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white">Open / Switch <ArrowRight size={14} /></button><Link data-testid={`customer-website-dashboard-${w.slug}`} to={`/account/websites/${w.slug}/overview`} className="rounded-lg border px-4 py-2 text-sm font-semibold">Dashboard</Link><Link data-testid={`customer-website-billing-${w.slug}`} to={`/account/websites/${w.slug}/billing`} className="inline-flex items-center gap-1 rounded-lg border px-4 py-2 text-sm font-semibold"><Receipt size={14} /> Billing / Invoices</Link></div>
              </article>
            ))}</div>
          )}
        </section>
      </Container>
    </div>
  );
}
